import React, { useEffect, useRef } from 'react';
import {
    BrowserRouter as Router,
    Switch,
    Route,
    useLocation,
    Redirect
} from "react-router-dom";

import { AuthProvider } from "./auth/authContext";
import { QueryCache, ReactQueryCacheProvider } from 'react-query';
import { ApiProvider } from "./api/apiContext";
import { PlayerProvider } from "./player/playerContext";
import LandingPage from './landing/landing';
import RoundView from './roundview/roundview';
import CouponView from './couponview/couponview';
import RoundHistory from './roundhistory/roundhistory';
import "./translations/i18n";
import Syndicate from './syndicates/syndicate';
import CouponShareList from './couponsharelist/couponsharelist';
import Help from './help/help';

import { ThemeProvider, createTheme } from '@mui/material';
import { getTheme, getThemeVariant } from './theme/themehelper';
import PlayAgent from './syndicates/playagent';
import { getLanguage } from './translations/languagehelper';
import { initTheme } from './theme/inittheme';
import { operatorC } from "./common/operatorhelpers";
import i18n from "i18next";
import ErrorBoundaryWithHook from './common/ErrorBoundary';
import ApiBetting from './apibetting/apibetting';
import SyndicateResult from './syndicates/syndicateresult';
import FreeBetView from './freebet/freebetview';
import TerminalAgent from './terminal/terminalagent';
import TerminalCouponAgent from './terminal/terminalcouponagent';
import TerminalWrapper from './terminal/terminalwrapper';
import PublicCoupon from './couponview/publiccoupon';
import TerminalDisplay from './terminal/terminaldisplay';
import { initAnalytics, track } from './analytics/analytics';
import AnalyticsTracker from './analytics/analyticstracker';

const POOLX_MESSAGE_SOURCE = 'poolx_game_client';
const OPERATOR_MESSAGE_SCROLL_TO_TOP = 'scroll_to_top';

const OPERATOR_MESSAGE_SCROLL_TO_TOP_DEF_DELAY = 200
const OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY = 200;
const OPERATOR_MESSAGE_CONTENT_RESIZE_DEF_DELAY = 200;
const OPERATOR_MESSAGE_ERROR_DEF_DELAY = 200;

let useExtendedNoticeFormat = false;

export const createMessageId = () => {
    return window.performance.timeOrigin + window.performance.now();
}

export const operatorAdviceScrollToTop = (delay) => {
    // advise operator that it is desirable to scroll the page to the top
    let operatorMessage = OPERATOR_MESSAGE_SCROLL_TO_TOP;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "action",
            message_type: "scroll_to_top",
            message_id: createMessageId(),
        };
    }

    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicate no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_SCROLL_TO_TOP_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }
    //console.debug('sending scroll to top(' + actualDelay + ')');
    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdviceBetSuccess = (data, delay) => {
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "bet_placed",
            message_id: createMessageId(),
            payload: {
                amount: data.amount,
                currency: data.currency,
                transactionId: data.transactionId,
                roundId: data.roundId,
            }
        };
    }
    //console.debug("posting 'bet_success' message to the parent window", operatorMessage, targetOrigin)
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicate no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdviceCancelSuccess = (data, delay) => {
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "bet_canceled",
            message_id: createMessageId(),
            payload: {
                transactionId: data.transactionId,
                roundId: data.roundId,
            }
        };
    }
    //console.debug("posting 'bet_success' message to the parent window", operatorMessage, targetOrigin)
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicates no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

// Must be posted synchronously before the cancel request is issued, so the operator can act on it
// without racing the API call. Call with delay -1 - a late resend could trigger a duplicate cancel.
export const operatorAdviceCancelInitiated = (data, delay) => {
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "cancel_initiated",
            message_id: createMessageId(),
            payload: {
                couponShareId: data.couponShareId,
                roundId: data.roundId,
                amount: data.amount,
                currency: data.currency,
            }
        };
    }
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicates no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdviceCancelFailed = (data, delay) => {
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "cancel_failed",
            message_id: createMessageId(),
            payload: {
                couponShareId: data.couponShareId,
                errorCode: data.errorCode,
                errorMessage: data.errorMessage,
                operatorErrorCode: data.operatorErrorCode,
            }
        };
    }
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicates no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

// Posted synchronously before the purchase request is issued. Call with delay -1, see above.
export const operatorAdviceBetInitiated = (data, delay) => {
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "bet_initiated",
            message_id: createMessageId(),
            payload: {
                syndicateId: data.syndicateId,
                roundId: data.roundId,
                amount: data.amount,
                currency: data.currency,
            }
        };
    }
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicates no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdvicePurchase = (data, delay) => {
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "purchase",
            message_id: createMessageId(),
            payload: {
                ticketName: data.ticketName,
                amount: data.amount,
                currency: data.currency,
                status: data.status,
            }
        };
    }
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicate no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdviceLogin = (data, delay) => {
    track("login_prompt_shown", { navigation_key: data.navigationKey, round_id: data.roundId });
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "login_user",
            message_id: createMessageId(),
            payload: {
                navigationId: data.navigationId,
                navigationKey: data.navigationKey,
                roundId: data.roundId
            }
        };
    }
    //console.debug("posting 'bet_success' message to the parent window", operatorMessage, targetOrigin)
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicate no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_BET_SUCCESS_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdviceContentResize = (data, delay) => {
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "action",
            message_type: "resize_content",
            message_id: createMessageId(),
            payload: {
                width: data.width,
                height: data.height,
                units: data.units,
            }
        };
    }
    //console.info("posting 'resize' message to the parent window", data)
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicate no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_CONTENT_RESIZE_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdviceErrorOccurred = (data, delay) => {
    track("error_shown", { error_code: data.errorCode, operator_error_code: data.operatorErrorCode });
    let operatorMessage = data;
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "error_occurred",
            message_id: createMessageId(),
            payload: {
                error_code: data.errorCode,
                error_message: data.errorMessage,
                operator_error_code: data.operatorErrorCode,
            }
        };
    }
    window.parent.postMessage(operatorMessage, targetOrigin);

    if (delay && delay <= 0) {
        // Explicitly negative delay indicate no resend
        return
    }
    let actualDelay = OPERATOR_MESSAGE_ERROR_DEF_DELAY;
    if (delay) {
        actualDelay = delay
    }

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

export const operatorAdviceReload = () => {
    let operatorMessage = "reload_client";
    let targetOrigin = '*';
    if (useExtendedNoticeFormat) {
        targetOrigin = '*';
        operatorMessage = {
            source: POOLX_MESSAGE_SOURCE,
            level: "notice",
            message_type: "reload_client",
            message_id: createMessageId(),
            payload: {
            }
        };
    }
    window.parent.postMessage(operatorMessage, targetOrigin);
    let actualDelay = OPERATOR_MESSAGE_ERROR_DEF_DELAY;

    setTimeout(() => {
        window.parent.postMessage(operatorMessage, targetOrigin);
    }, actualDelay)
}

const handleRouteChange = () => {
    //console.debug('handleRouteChange calling scroll to top');
    operatorAdviceScrollToTop(500);
}

// improvised component to hook into router context
const RouteSwitchListener = () => {
    let location = useLocation();
    useEffect(() => {
        handleRouteChange(location);
    }, [location])

    return <div />
}

function App() {
    const urlParams = new URLSearchParams(window.location.search);
    let languageSetTo = 'en';
    if (urlParams.size > 0) {
        languageSetTo = getLanguage(urlParams);
        i18n.changeLanguage(languageSetTo);
    }

    let operatorId;
    let operatorToken;
    let operatorGameId;
    let operatorPlayerId;
    let operatorCurrency;
    let operatorLanguage;
    let operatorChannel;
    let operatorJurisdiction;
    let operatorExtraData;
    let homeUrl;
    let roundId;
    let syndicateId;
    let couponId;
    let agentId;
    let syndicateResultId;
    let terminalAgentId;
    let terminalCouponAgentId;
    let terminalDisplayAgentId;
    let terminalAgentIdToSet;
    let publicCouponId;
    let areaId;
    let roundCouponSigns;

    let navigationKey = urlParams.get("navigationKey");
    let navigationId = urlParams.get("navigationId");

    switch (navigationKey) {
        case "round":
            roundId = navigationId;
            break;
        case "roundcoupon": {
            // Format: <roundId>_<leg1>-<leg2>-...-<legN>
            const separatorIndex = navigationId ? navigationId.indexOf("_") : -1;
            roundId = separatorIndex > -1 ? navigationId.substring(0, separatorIndex) : navigationId;
            roundCouponSigns = separatorIndex > -1 ? navigationId.substring(separatorIndex + 1) : undefined;
            break;
        }
        case "area":
            areaId = navigationId;
            break;
        case "syndicate":
            syndicateId = navigationId;
            break;
        case "coupon":
            couponId = navigationId;
            break;
        case "agent":
            agentId = navigationId;
            break;
        case "syndicateresult":
            syndicateResultId = navigationId;
            break;
        case "terminalagent":
            terminalAgentId = navigationId;
            window.isTerminal = true;
            terminalAgentIdToSet = navigationId;
            break;
        case "terminalcouponagent":
            terminalCouponAgentId = navigationId;
            window.isTerminal = true;
            terminalAgentIdToSet = navigationId;
            break;
        case "terminaldisplay":
            terminalDisplayAgentId = navigationId;
            window.isTerminal = true;
            terminalAgentIdToSet = navigationId;
            break;
        case "publiccouponid":
            publicCouponId = navigationId;
            break;
        default:
            break;
    }


    operatorId = urlParams.get("operatorId");
    let compatMode = operatorId && (operatorId === operatorC.Mockop || operatorId === operatorC.EuroOperator);
    if (compatMode) {
        operatorId = urlParams.get("operatorId");
        operatorToken = urlParams.get("operatorToken");
        operatorPlayerId = urlParams.get("operatorPlayerId");
        homeUrl = urlParams.get('homeUrl');
        operatorExtraData = urlParams.get("extraData");
        operatorChannel = urlParams.get("operatorChannel");
    } else {
        operatorId = urlParams.get("operatorid");
        operatorToken = urlParams.get("token");
        operatorGameId = urlParams.get("gameid");
        operatorPlayerId = urlParams.get("playerid");
        operatorCurrency = urlParams.get("currency");
        operatorLanguage = urlParams.get("language");
        operatorChannel = urlParams.get("channel");
        operatorJurisdiction = urlParams.get("jurisdiction");
    }


    if (!operatorGameId) {
        operatorGameId = 'poolx';
    }
    if (!operatorPlayerId) {
        operatorPlayerId = '';
    }
    if (!operatorCurrency) {
        operatorCurrency = '';
    }
    if (!operatorLanguage) {
        operatorLanguage = '';
    }
    if (!operatorChannel) {
        operatorChannel = 'online';
    }
    if (!operatorJurisdiction) {
        operatorJurisdiction = '';
    }
    if (!operatorExtraData) {
        operatorExtraData = '';
    }
    if (!homeUrl) {
        homeUrl = '';
    }

    // TODO find a proper way to do this
    if (operatorId && (operatorId === "EXPEKTSE" || operatorId === "EXPEKTDK" ||  operatorId === "BETMGMSE" || operatorId === "LEOVEGASSE" || operatorId === "LEOVEGASDK" || operatorId === "GOGOSE" || operatorId === "UNIBETSI" || operatorId === "UNIBET")) {
        useExtendedNoticeFormat = true;
    }

    // FIXME a hack to allow testing in DK (force Danish language)
    if (operatorId && (operatorId === "EXPEKTDK" ||  operatorId === "LEOVEGASDK")) {
        operatorLanguage = 'da';
    }

    let pTheme = 'green';

    if (urlParams.size !== 0) {
        pTheme = getTheme(urlParams, languageSetTo, operatorId)
        const pThemeVariant = getThemeVariant(urlParams, operatorId);
        initTheme(pTheme, pThemeVariant);
        window.operatorId = operatorId;
        window.theme = pTheme;
        window.themeVariant = pThemeVariant;
        window.nativeApp = urlParams.get("nativeApp") === "true";
    }

    initAnalytics({
        operator_id: operatorId || '',
        theme: pTheme,
        language: languageSetTo,
        currency: operatorCurrency,
        channel: operatorChannel,
        entry: navigationKey || 'direct',
    }, operatorPlayerId);

    const theme = createTheme({
        name: pTheme,
        components: {
            MuiTypography: {
                styleOverrides: {
                    root: {
                        fontFamily: 'var(--font-body)',
                        fontStyle: 'var(--font-style)',
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontFamily: 'var(--font-body)',
                        fontSize: "12px"
                    },
                },
            },
        },
        palette: {
            primary: {
                main: '#2196F3',
            },
            secondary: {
                main: '#FFC107',
            },
        },
    });

    const appRef = useRef(null)
    // Posting resize messages to the parent window for operators that don't want to use 'iframe-resizer`
    useEffect(() => {
        const element = appRef?.current;
        if (!element) return;

        const observer = new ResizeObserver((entries) => {
            // Do something when the element is resized
            const width = entries[0]?.contentBoxSize[0]?.inlineSize;
            const height = entries[0]?.contentBoxSize[0]?.blockSize;

            if (entries[0]?.target.querySelector('.NoResize')) {
                return;
            }

            if (width || height) {
                let contentResizeEvent = {
                    type: "content_resize",
                    height: height,
                    width: width,
                    units: "px",
                }
                operatorAdviceContentResize(contentResizeEvent)
            }
        });

        observer.observe(element);
        return () => {
            // Cleanup the observer by unobserving all elements
            observer.disconnect();
        };
    }, [])

    const onAuthError = (response) => {
        let errorCode = 101;
        let operatorErrorCode = 0;

        if (response.errorCode !== undefined) {
            errorCode = response.errorCode;
        }

        if (response.operatorErrorCode !== undefined) {
            operatorErrorCode = response.operatorErrorCode;
        }

        operatorAdviceErrorOccurred({
            errorCode: errorCode,
            errorMessage: "Your session has expired",
            operatorErrorCode: operatorErrorCode
        })

    }


    return (

        <ThemeProvider theme={theme}>
            <ReactQueryCacheProvider queryCache={new QueryCache()}>

                <AuthProvider
                    operatorId={operatorId}
                    operatorGameId={operatorGameId}
                    operatorPlayerId={operatorPlayerId}
                    operatorChannel={operatorChannel}
                    operatorLanguage={operatorLanguage}
                    operatorToken={operatorToken}
                    operatorCurrency={operatorCurrency}
                    operatorJurisdiction={operatorJurisdiction}
                    operatorExtraData={operatorExtraData}
                    homeUrl={homeUrl}
                    agentId={terminalAgentIdToSet}
                    onAuthError={onAuthError}
                >
                    <ApiProvider>
                        <PlayerProvider>
                            <ErrorBoundaryWithHook>
                                <div className="wrapper" ref={appRef}>
                                    <Router>

                                        <RouteSwitchListener />
                                        <AnalyticsTracker />
                                        <Switch>
                                            <Route path={"/apibetting"}>
                                                <ApiBetting />
                                            </Route>
                                            <Route path="/:tipsId/rounds/:roundId">
                                                <RoundView />
                                            </Route>
                                            <Route path="/rounds/:roundId">
                                                <RoundView />
                                            </Route>
                                            <Route path="/mycoupons">
                                                <CouponShareList />
                                            </Route>
                                            <Route path="/roundhistory">
                                                <RoundHistory />
                                            </Route>
                                            <Route path="/coupon/:couponId">
                                                <CouponView />
                                            </Route>
                                            <Route path="/playagent">
                                                <PlayAgent agentId={0} />
                                            </Route>
                                            <Route path="/syndicate/:syndicateId">
                                                <Syndicate />
                                            </Route>
                                            <Route path="/syndicateresult/:syndicateId"
                                                render={(props) => {
                                                    return (
                                                        <SyndicateResult syndicateId={props.match.params.syndicateId} />
                                                    )
                                                }}   >
                                            </Route>
                                            <Route path="/terminalagent/:agentId"
                                                render={(props) => {
                                                    return (
                                                        <TerminalWrapper agentId={props.match.params.agentId} />
                                                    )
                                                }}   >
                                            </Route>
                                            <Route path="/terminalcouponagent/:agentId"
                                                render={(props) => {
                                                    return (
                                                        <TerminalWrapper agentId={props.match.params.agentId} />
                                                    )
                                                }}   >
                                            </Route>
                                            <Route path="/terminaldisplay/:agentId"
                                                render={(props) => {
                                                    return (
                                                        <TerminalDisplay agentId={props.match.params.agentId} />
                                                    )
                                                }}   >
                                            </Route>
                                            <Route path="/freebet/:freebetId"
                                                render={(props) => {
                                                    return (
                                                        <FreeBetView freebetId={props.match.params.freebetId} />
                                                    )
                                                }}   >
                                            </Route>
                                            <Route path="/syndicatesforagent/:agentId"
                                                render={(props) => {
                                                    return (
                                                        <PlayAgent agentId={props.match.params.agentId} />
                                                    )
                                                }}>
                                            </Route>
                                            <Route path="/publiccoupon/:publicCouponId"
                                                render={(props) => {
                                                    return (
                                                        <PublicCoupon publicCouponId={props.match.params.publicCouponId} />
                                                    )
                                                }}   >
                                            </Route>
                                            <Route path="/help">
                                                <Help />
                                            </Route>
                                            <Route
                                                path="/clear"
                                                render={() => {
                                                    roundId = undefined;
                                                    roundCouponSigns = undefined;
                                                    syndicateId = undefined;
                                                    couponId = undefined;
                                                    agentId = undefined;
                                                    return (
                                                        <LandingPage />
                                                    )
                                                }}
                                            />
                                            <Route
                                                path="/"
                                                render={() => {

                                                    if (areaId) {
                                                        switch (areaId) {
                                                            case "rounds":
                                                                return (<Redirect to={"/roundhistory"} />);
                                                            case "syndicates":
                                                                return (<Redirect to={"/playagent"} />);
                                                            case "coupons":
                                                                return (<Redirect to={"/mycoupons"} />);
                                                            default:
                                                                return (<LandingPage />);
                                                        }
                                                    }

                                                    if (syndicateId) {
                                                        return (
                                                            <Redirect to={"/syndicate/" + syndicateId} />
                                                        );
                                                    }

                                                    if (couponId) {
                                                        return (
                                                            <Redirect to={"/coupon/" + couponId} />
                                                        );
                                                    }

                                                    if (agentId) {
                                                        return (
                                                            <Redirect to={"/syndicatesforagent/" + agentId} />
                                                        );
                                                    }

                                                    if (syndicateResultId) {
                                                        return (
                                                            <Redirect to={"/syndicateresult/" + syndicateResultId} />
                                                        );
                                                    }

                                                    if (terminalAgentId) {
                                                        return (
                                                            <Redirect to={"/terminalagent/" + terminalAgentId} />
                                                        );
                                                    }

                                                    if (terminalCouponAgentId) {
                                                        return (
                                                            <Redirect to={"/terminalcouponagent/" + terminalCouponAgentId} />
                                                        );
                                                    }

                                                    if (terminalDisplayAgentId) {
                                                        return (
                                                            <Redirect to={"/terminaldisplay/" + terminalDisplayAgentId} />
                                                        );
                                                    }

                                                    if (publicCouponId) {
                                                        return (
                                                            <Redirect to={"/publiccoupon/" + publicCouponId} />
                                                        );
                                                    }

                                                    return (
                                                        roundId ?
                                                            <Redirect to={"/rounds/" + roundId + (roundCouponSigns ? "?signs=" + encodeURIComponent(roundCouponSigns) : "")} /> :
                                                            <LandingPage />
                                                    )
                                                }}
                                            />

                                        </Switch>
                                    </Router>
                                </div>
                            </ErrorBoundaryWithHook>
                        </PlayerProvider>
                    </ApiProvider>
                </AuthProvider>

            </ReactQueryCacheProvider>
        </ThemeProvider>

    );
}

export default App;
